import React from 'react';
import { Button, ButtonProps, CircularProgress, Box } from '@mui/material';

interface ActionButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'outline' | 'ghost';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  size?: 'small' | 'medium' | 'large';
}

const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  variant = 'primary',
  loading = false,
  icon,
  iconPosition = 'start',
  size = 'medium',
  disabled,
  sx,
  ...props
}) => {
  const getVariantProps = () => {
    switch (variant) {
      case 'primary':
        return { variant: 'contained' as const, color: 'primary' as const };
      case 'secondary':
        return { variant: 'contained' as const, color: 'secondary' as const };
      case 'success':
        return { variant: 'contained' as const, sx: { backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } } };
      case 'warning':
        return { variant: 'contained' as const, sx: { backgroundColor: '#ed6c02', '&:hover': { backgroundColor: '#e65100' } } };
      case 'error':
        return { variant: 'contained' as const, sx: { backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#c62828' } } };
      case 'outline':
        return { variant: 'outlined' as const };
      case 'ghost':
        return { 
          variant: 'text' as const, 
          sx: { 
            '&:hover': { 
              backgroundColor: 'rgba(0,0,0,0.04)' 
            } 
          } 
        };
      default:
        return { variant: 'contained' as const };
    }
  };

  const getSizeProps = () => {
    switch (size) {
      case 'small':
        return { size: 'small' as const, sx: { px: 2, py: 0.75, fontSize: '0.75rem' } };
      case 'large':
        return { size: 'large' as const, sx: { px: 4, py: 1.5, fontSize: '1rem' } };
      default:
        return { size: 'medium' as const };
    }
  };

  const variantProps = getVariantProps();
  const sizeProps = getSizeProps();

  return (
    <Button
      {...props}
      {...variantProps}
      {...sizeProps}
      disabled={disabled || loading}
      sx={{
        borderRadius: 2,
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: variant === 'ghost' ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: variant === 'ghost' ? 'none' : '0 4px 8px rgba(0,0,0,0.15)',
        },
        ...sx,
        ...variantProps.sx,
        ...sizeProps.sx,
      }}
    >
      {loading ? (
        <CircularProgress size={20} color="inherit" />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && iconPosition === 'start' && icon}
          {children}
          {icon && iconPosition === 'end' && icon}
        </Box>
      )}
    </Button>
  );
};

export default ActionButton;
